import _ from "lodash";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";
import useRowClassName from "../../hooks/useRowClassName";
import IEntityFieldsProps from "../../interfaces/IEntityFieldsProps";
import { ILeaseCompState, LeaseCompPropertyType, LeaseCompSource, LeaseType } from "../../interfaces/ILeaseComp";
import { leaseCompPropertyTypeToString, leaseCompSourceToString, leaseTypeToString } from "../../utils/enumUtils";
import commonStyles from "../Common.module.css";
import editStyles from "../Edit.module.css";
import AddressInput from "../inputs/AddressInput";
import DateInput from "../inputs/DateInput";
import NumberInput from "../inputs/NumberInput";
import PipedriveCompanyInput from "../inputs/PipedriveCompanyInput";
import SelectInput from "../inputs/SelectInput";
import StringInput from "../inputs/StringInput";
import TextInput from "../inputs/TextInput";

function LeaseCompFields({ formik }: IEntityFieldsProps<ILeaseCompState>) {
    const rowClassName = useRowClassName();

    const onTenantLandSizeChange = useCallback((value: number | null) => ({
        landPsf: calculateLandPsf(formik.values.totalRent, value),
    }), [formik.values]);

    const onBuildingSizeChange = useCallback((value: number | null) => ({
        buildingPsf: calculateBuildingPsf(formik.values.totalRent, value),
    }), [formik.values]);

    const onTotalRentChange = useCallback((value: number | null) => ({
        landPsf: calculateLandPsf(value, formik.values.tenantLandSize),
        buildingPsf: calculateBuildingPsf(value, formik.values.buildingSize),
    }), [formik.values]);

    return (
        <div className={editStyles.container}>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Lease Details</div>
                <div className={rowClassName}>
                    <AddressInput formik={formik} label="ADDRESS" name="address" />
                </div>
                <div className={rowClassName}>
                    <PipedriveCompanyInput formik={formik} label="TENANT" name="pipedriveCompanyName" />
                    <NumberInput formik={formik} label="ANNUAL RENT ($)" name="totalRent" onChange={onTotalRentChange} />
                    <DateInput formik={formik} label="DATE SIGNED" name="signedDate" />
                </div>
                <div className={rowClassName}>
                    <DateInput formik={formik} label="LEASE EXPIRATION" name="leaseExpiration" />
                    <NumberInput formik={formik} label="LAND $/ACRE" name="landPsf" />
                    <NumberInput formik={formik} label="BUILDING $/PSF" name="buildingPsf" />
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Other Terms</div>
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="TYPE" name="type" valueAsCorrectType={value => value as LeaseType}>
                        <option value="">----</option>
                        <option value={LeaseType.Gross}>{leaseTypeToString(LeaseType.Gross)}</option>
                        <option value={LeaseType.Modified}>{leaseTypeToString(LeaseType.Modified)}</option>
                        <option value={LeaseType.NNN}>{leaseTypeToString(LeaseType.NNN)}</option>
                        <option value={LeaseType.SeeNotes}>{leaseTypeToString(LeaseType.SeeNotes)}</option>
                    </SelectInput>
                    <NumberInput formik={formik} label="YEAR" name="year" />
                    <NumberInput formik={formik} label="TI ALLOWANCE ($)" name="ti" />
                </div>
                <div className={rowClassName}>
                    <div className={editStyles.doubleWidthRowGroup}>
                        <div className={rowClassName}>
                            <StringInput formik={formik} label="ESCALATIONS" name="escalations" />
                            <StringInput formik={formik} label="OPTIONS" name="options" />
                        </div>
                    </div>
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Property Details</div>
                <div className={rowClassName}>
                    <NumberInput formik={formik} label="TENANT LAND SIZE (ACRES)" name="tenantLandSize" onChange={onTenantLandSizeChange} />
                    <NumberInput formik={formik} label="PARCEL SIZE (ACRES)" name="parcelSizeAcres" />
                    <NumberInput formik={formik} label="BUILDING SIZE (SQ-FT)" name="buildingSize" onChange={onBuildingSizeChange} />
                </div>
                <div className={rowClassName}>
                    <div className={editStyles.doubleWidthRowGroup}>
                        <div className={rowClassName}>
                            <StringInput formik={formik} label="ZONING" name="zoning" />
                            <SelectInput formik={formik} label="PROPERTY TYPE" name="propertyType" valueAsCorrectType={value => value as LeaseCompPropertyType}>
                                <option value="">----</option>
                                <option value={LeaseCompPropertyType.IOSNoBuilding}>{leaseCompPropertyTypeToString(LeaseCompPropertyType.IOSNoBuilding)}</option>
                                <option value={LeaseCompPropertyType.IOSWithBuilding}>{leaseCompPropertyTypeToString(LeaseCompPropertyType.IOSWithBuilding)}</option>
                                <option value={LeaseCompPropertyType.SmallBay}>{leaseCompPropertyTypeToString(LeaseCompPropertyType.SmallBay)}</option>
                                <option value={LeaseCompPropertyType.Distribution}>{leaseCompPropertyTypeToString(LeaseCompPropertyType.Distribution)}</option>
                                <option value={LeaseCompPropertyType.SelfStorage}>{leaseCompPropertyTypeToString(LeaseCompPropertyType.SelfStorage)}</option>
                                <option value={LeaseCompPropertyType.RVBoatStorage}>{leaseCompPropertyTypeToString(LeaseCompPropertyType.RVBoatStorage)}</option>
                                <option value={LeaseCompPropertyType.TruckTerminal}>{leaseCompPropertyTypeToString(LeaseCompPropertyType.TruckTerminal)}</option>
                                <option value={LeaseCompPropertyType.Other}>{leaseCompPropertyTypeToString(LeaseCompPropertyType.Other)}</option>
                            </SelectInput>
                        </div>
                    </div>
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Internal</div>
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="COMP SOURCE" name="compSource" valueAsCorrectType={value => value as LeaseCompSource}>
                        <option value="">----</option>
                        <option value={LeaseCompSource.PropertyOwner}>{leaseCompSourceToString(LeaseCompSource.PropertyOwner)}</option>
                        <option value={LeaseCompSource.TransactionBroker}>{leaseCompSourceToString(LeaseCompSource.TransactionBroker)}</option>
                        <option value={LeaseCompSource.Tenant}>{leaseCompSourceToString(LeaseCompSource.Tenant)}</option>
                        <option value={LeaseCompSource.ThirdPartyBroker}>{leaseCompSourceToString(LeaseCompSource.ThirdPartyBroker)}</option>
                        <option value={LeaseCompSource.Other}>{leaseCompSourceToString(LeaseCompSource.Other)}</option>
                    </SelectInput>
                </div>
                <div className={rowClassName}>
                    <TextInput formik={formik} label="NOTES" name="notes" placeholder="Enter notes..." />
                </div>
            </div>
        </div>
    );
}

const calculateLandPsf = (totalRent: number | null, tenantLandSize: number | null): number | null => {
    return totalRent && tenantLandSize ? _.round(totalRent / tenantLandSize / 12, 2) : null;
}

const calculateBuildingPsf = (totalRent: number | null, buildingSize: number | null): number | null => {
    return totalRent && buildingSize ? _.round(totalRent / buildingSize, 2) : null;
}

export default observer(LeaseCompFields);
